import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { Ressource } from '../models/ressource.model';
import { RessourceCreateRequest } from '../requests/ressource-create-request.model';
import { RessourceUpdateRequest } from '../requests/ressource-update-request.model';
import { RessourceService } from '../services/ressource.service';
import { RessourceState } from './ressource.state';

export function avecMéthodesRessources() {
  return signalStoreFeature(
    { state: type<RessourceState>() },
    // Updater
    withMethods((store) => ({
      chargerRessources: (state: { ressources: Ressource[] }) => patchState(store, { ressources: state.ressources }),
      ajouterRessource: (state: { ressource: Ressource }) =>
        patchState(store, {
          ressources: [...store.ressources(), state.ressource],
        }),
      setFiltre: (filtre: string) => patchState(store, { filtre }),
    })),

    // Effects
    withMethods((store, ressourceService = inject(RessourceService)) => ({
      chargerToutes: rxMethod<string>(
        pipe(
          switchMap((idCartographie) =>
            ressourceService.findAll(idCartographie).pipe(
              tapResponse({
                next: (result) =>
                  store.chargerRessources({
                    ressources: result.items,
                  }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des interrelations, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      créer: rxMethod<{ idCartographie: string; data: RessourceCreateRequest; next: () => void }>(
        pipe(
          switchMap(({ idCartographie, data, next }) =>
            ressourceService.create(idCartographie, data).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la création de la ressource, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{ id: string; data: RessourceUpdateRequest; next: () => void }>(
        pipe(
          switchMap(({ id, data, next }) =>
            ressourceService.edit(id, data).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification de la ressource, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{ idRessource: string; next: () => void }>(
        pipe(
          switchMap(({ idRessource, next }) =>
            ressourceService.delete(idRessource).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression de la ressource, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
